/**
 * Lazy load images when they become visible on screen.
 * Uses IntersectionObserver to set src/srcset from data-src/data-srcset only when the image enters the viewport.
 * Handles both plain lazy images and .blur-up-image (progressive blur-up transition).
 *
 * Usage: add data-src (and optionally data-srcset, data-sizes) to an img.
 * For blur-up: use class "blur-up-image", initial src as placeholder, data-src as high-res URL.
 */
(function () {
  "use strict";

  var LAZY_SELECTOR = "img[data-src]:not(.lazy-loaded)";
  var ROOT_MARGIN = "100px"; // Start loading when within 100px of viewport
  var THRESHOLD = 0.01;

  var PROTOCOL =
    typeof location !== "undefined" && location.protocol
      ? location.protocol
      : "https:";

  /** Normalize protocol-relative URLs (//...) to https: so browser does not drop srcset candidates */
  function ensureAbsoluteUrl(url) {
    if (typeof url !== "string" || !url.trim()) return url;
    var t = url.trim();
    if (t.indexOf("//") === 0) return PROTOCOL + t;
    return url;
  }

  /** Valid descriptor: width (e.g. 600w) or density (e.g. 1x, 2x, 1.5x) */
  function isValidDescriptor(s) {
    if (!s || typeof s !== "string") return false;
    var t = s.trim();
    return /^\d+w$/.test(t) || /^\d+(\.\d+)?x$/.test(t);
  }

  /** URL is valid for srcset: starts with http:, https:, //, or / */
  function isValidSrcsetUrl(url) {
    if (!url || typeof url !== "string") return false;
    var t = url.trim();
    if (/^Liquid$/i.test(t)) return false;
    return (
      t.indexOf("http://") === 0 ||
      t.indexOf("https://") === 0 ||
      t.indexOf("//") === 0 ||
      t.indexOf("/") === 0
    );
  }

  /**
   * Parse srcset string, keep only valid URL + descriptor entries, normalize protocol, rejoin.
   * Fixes "unknown descriptor", "Dropped srcset candidate Liquid", and protocol-relative drops.
   */
  function normalizeSrcset(srcset) {
    if (!srcset || typeof srcset !== "string") return "";
    var parts = srcset.split(",");
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (!part) continue;
      var lastSpace = part.lastIndexOf(" ");
      var url = lastSpace === -1 ? part : part.slice(0, lastSpace).trim();
      var descriptor = lastSpace === -1 ? "" : part.slice(lastSpace).trim();
      if (!isValidSrcsetUrl(url)) continue;
      if (descriptor && !isValidDescriptor(descriptor)) descriptor = "";
      url = ensureAbsoluteUrl(url);
      out.push(descriptor ? url + " " + descriptor : url);
    }
    return out.join(", ");
  }

  function loadImage(img) {
    if (img.dataset.lazyLoaded === "true") return;
    var dataSrc = img.getAttribute("data-src");
    var dataSrcset = img.getAttribute("data-srcset");
    var dataSizes = img.getAttribute("data-sizes");
    if (!dataSrc) return;

    img.dataset.lazyLoaded = "true";
    img.setAttribute("src", ensureAbsoluteUrl(dataSrc));
    img.removeAttribute("data-src");
    var srcsetToSet = dataSrcset
      ? normalizeSrcset(dataSrcset)
      : normalizeSrcset(img.getAttribute("srcset") || "");
    if (srcsetToSet) {
      img.setAttribute("srcset", srcsetToSet);
    }
    if (dataSrcset) img.removeAttribute("data-srcset");
    if (dataSizes) {
      img.setAttribute("sizes", dataSizes);
      img.removeAttribute("data-sizes");
    }
    img.classList.add("lazy-loaded");

    // Blur-up: add .loaded when new image has loaded so CSS transition runs
    if (img.classList.contains("blur-up-image")) {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add("loaded");
      } else {
        img.addEventListener(
          "load",
          function onLoad() {
            img.classList.add("loaded");
            img.removeEventListener("load", onLoad);
          },
          { once: true },
        );
        setTimeout(function () {
          if (!img.classList.contains("loaded")) img.classList.add("loaded");
        }, 2000);
      }
    }
  }

  function initLazyImages(container) {
    container = container || document;
    var images = container.querySelectorAll(
      container === document
        ? LAZY_SELECTOR
        : "img[data-src]:not(.lazy-loaded)",
    );
    if (images.length === 0) return;

    if (!("IntersectionObserver" in window)) {
      images.forEach(function (img) {
        loadImage(img);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var img = entry.target;
            loadImage(img);
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: ROOT_MARGIN, threshold: THRESHOLD },
    );

    images.forEach(function (img) {
      // Skip images that should load eagerly (LCP candidates)
      if (
        img.getAttribute("loading") === "eager" ||
        img.getAttribute("fetchpriority") === "high"
      ) {
        loadImage(img);
      } else {
        observer.observe(img);
      }
    });
  }

  function onReady() {
    initLazyImages();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }

  document.addEventListener("shopify:section:load", function (e) {
    if (e.target) initLazyImages(e.target);
  });
})();
